import * as React from "react";
import {
  makeStyles,
  Textarea,
  Button,
  Field,
  Switch,
  Input,
  Spinner,
  buttonClassNames,
  tokens,
  Label,
  SpinButton,
  Text,
} from "@fluentui/react-components";
import { CheckmarkCircle16Filled, ErrorCircle16Filled } from "@fluentui/react-icons";

/**
 * Interface définissant les propriétés du composant StandardisationPage.
 */
interface StandardisationProps {
  handleMenuReload: () => void; // Fonction pour recharger le menu principal
  standardiseOrExtractData: (
    cellsSearched: string[],
    offset: number,
    standardFormat: string,
    calledFunction: string
  ) => any; // Fonction pour standardiser ou extraire des données
  calledFunction: string; // Fonction appelée (standardisation ou extraction)
}

/**
 * Fonction utilitaire pour convertir une chaîne de lettres (colonnes Excel) en un numéro.
 * Par exemple, "A" devient 1, "Z" devient 26, "AA" devient 27, etc.
 * @param {String} str - La chaîne de lettres à convertir.
 * @returns {number} - Le numéro correspondant à la colonne.
 */
function convertLettersToNumber(str: String) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let result = 0;
  const reversedStr = str.toUpperCase().split("").reverse(); // Inverse la chaîne pour calculer les poids
  for (let i = 0; i < reversedStr.length; i++) {
    const char = reversedStr[i];
    const position = alphabet.indexOf(char) + 1; // Position de la lettre dans l'alphabet (1-indexé)
    result += position * Math.pow(26, i); // Poids basé sur la position (base 26)
  }
  return result;
}

/**
 * Interface définissant les propriétés d'un dictionnaire interne pour les textes d'interface.
 */
interface InnerDictionary {
  title: string; // Titre de la section
  description: string; // Description de la section
  placeholder: string; // Texte d'exemple ou d'instruction
  errorMissingInfo: string; // Message d'erreur si des informations manquent
  manualSelectionTitle: string; // Titre pour la sélection manuelle
}

/**
 * Interface définissant les propriétés d'un dictionnaire externe contenant plusieurs sections.
 */
interface OuterDictionary {
  [key: string]: InnerDictionary; // Clé dynamique pour accéder aux sections
}

/**
 * Dictionnaire contenant les textes d'interface pour les différentes fonctionnalités.
 * Utilisé pour afficher dynamiquement les titres, descriptions, et messages d'erreur.
 */
const interfaceShownTextStrings: OuterDictionary = {
  extract: {
    title: "Extraction d'informations", // Titre pour l'extraction
    description: "Informations à extraire du document", // Description de l'extraction
    placeholder: "Je veux une liste de tous les noms de villes dans les cases sélectionnées", // Exemple d'utilisation
    errorMissingInfo: "Assurez-vous d'écrire une requête descriptive pour votre assistant.", // Message d'erreur
    manualSelectionTitle: "Sélection manuelle des données à extraire", // Titre pour la sélection manuelle
  },
  standardise: {
    title: "Uniformiser des données", // Titre pour la standardisation
    description: "Format désiré des données standardisées", // Description de la standardisation
    placeholder:
      "Je veux que tous les numéros de téléphones dans les cases sélectionnées suivent ce format standard: +1 (514) 123-9912", // Exemple d'utilisation
    errorMissingInfo: "Assurez-vous d'écrire une requête descriptive pour votre assistant.", // Message d'erreur
    manualSelectionTitle: "Sélection manuelle des données à standardiser", // Titre pour la sélection manuelle
  },
};

/**
 * Composant StandardisationPage : permet de standardiser ou d'extraire des données dans Excel.
 * L'utilisateur peut choisir entre une sélection manuelle ou automatique des cellules.
 */
const StandardisationPage: React.FC<StandardisationProps> = (props: StandardisationProps) => {
  const styles = useStyles();
  // États locaux
  const [standardFormat, setStandardFormat] = React.useState(""); // Format standardisé ou requête
  const [checked, setChecked] = React.useState(true); // Mode de sélection (Excel ou manuel)
  const [fromCell, setFromCell] = React.useState(""); // Cellule de départ pour la sélection manuelle
  const [toCell, setToCell] = React.useState(""); // Cellule de fin pour la sélection manuelle
  const [offset, setOffset] = React.useState(0); // Décalage pour placer les résultats
  const [errors, setErrors] = React.useState(""); // Message d'erreur
  const [isDisabled, setIsDisabled] = React.useState(true); // Désactivation du bouton d'envoi
  const [errorMessage, setErrorMessage] = React.useState<string>(""); // Message d'erreur pour l'entrée utilisateur
  const calledFunction = props.calledFunction; // Fonction appelée (standardisation ou extraction)

  // État pour gérer l'animation de chargement
  const [loadingState, setLoadingState] = React.useState<LoadingState>("initial");
  type LoadingState = "initial" | "loading" | "loaded";

  // Contenu et icône du bouton en fonction de l'état de chargement
  const buttonContent =
    loadingState === "loading" ? "En cours..." : loadingState === "loaded" ? "Terminé" : "Envoyer";

  const buttonIcon =
    loadingState === "loading" ? (
      <Spinner size="tiny" />
    ) : loadingState === "loaded" ? (
      <CheckmarkCircle16Filled className={styles.successText} />
    ) : null;

  /**
   * Gère le changement entre le mode "sélection Excel" et "sélection manuelle".
   */
  const onChange = React.useCallback(
    (ev: any) => {
      setChecked(ev.currentTarget.checked);
    },
    [setChecked]
  );

  /**
   * Valide l'entrée utilisateur pour le format standardisé ou la requête.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.trim() === "") {
      setErrorMessage(
        "Votre entrée est vide. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else if (value.trim().length < 7) {
      setErrorMessage(
        "Veuillez entrer au moins 7 caractères. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else {
      setErrorMessage("");
      setIsDisabled(false);
    }
  };

  /**
   * Gère les changements dans les champs de sélection manuelle des cellules.
   */
  const handleCellChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setCell: React.Dispatch<React.SetStateAction<string>>
  ) => {
    let value = e.target.value.toUpperCase();
    const cellRegex = /^([A-Z]{1,3})([1-9][0-9]{0,6})?$/;
    const match = value.match(cellRegex);

    if (match) {
      let column = match[1];
      let row = match[2] ? parseInt(match[2]) : NaN;

      const columnNumber = convertLettersToNumber(column);
      const maxColumnNumber = convertLettersToNumber("XFD");

      if (columnNumber > maxColumnNumber) {
        column = "XFD";
      }

      if (!isNaN(row)) {
        row = Math.min(Math.max(row, 1), 1048576);
      }

      value = `${column}${row || ""}`;
      setCell(value);
    } else if (value === "") {
      setCell(value);
    }
  };

  const handleFromCellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleCellChange(e, setFromCell);
  };

  const handleToCellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleCellChange(e, setToCell);
  };

  const logToExcel = (message: string) => {
    Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const logCell = sheet.getRange("A1");
      logCell.values = [[message]];
      await context.sync();
    }).catch(console.error);
  };

  /**
   * Fonction principale pour standardiser ou extraire les données.
   */
  const StandardiseSelection = async () => {
    let cellsToStandardise: Array<string> = new Array<string>();
    if (!standardFormat) {
      setErrors(interfaceShownTextStrings[calledFunction]["errorMissingInfo"]);
    } else if (!checked && !(fromCell && toCell)) {
      setErrors("Veuillez selectionner les cellules affectées.");
    } else {
      setErrors("");
      setLoadingState("loading");
      if (!checked) {
        cellsToStandardise.push(fromCell);
        cellsToStandardise.push(toCell);
      }
      const successCode: any = await props.standardiseOrExtractData(
        cellsToStandardise,
        offset,
        standardFormat,
        props.calledFunction
      );
      if (successCode === -1) {
        setLoadingState("initial");
        setErrors("Crédits insuffisants.");
        return;
      }
      setLoadingState("loaded");
      setTimeout(() => {
        setLoadingState("initial");
        props.handleMenuReload();
      }, 1200);
    }
  };

  return (
    <div className={styles.container}>
      {/* Titre */}
      <p className={styles.text}>{interfaceShownTextStrings[calledFunction]["title"]}</p>

      {/* Champ pour le format standardisé ou la requête */}
      <Field
        label={interfaceShownTextStrings[calledFunction]["description"]}
        className={styles.fieldPadding}
      >
        <Textarea
          placeholder={interfaceShownTextStrings[calledFunction]["placeholder"]}
          size="medium"
          resize="none"
          onChange={(e: any) => {
            setStandardFormat(e.target.value);
            handleChange(e);
          }}
        />
        {errorMessage && (
          <div className={styles.errorText}>
            <ErrorCircle16Filled style={{ marginRight: "8px" }} />
            <Text>{errorMessage}</Text>
          </div>
        )}
      </Field>

      {/* Décalage pour placer les résultats */}
      <Field label="Décalage de colonne pour placer la réponse" className={styles.fieldPadding}>
        <SpinButton
          value={offset}
          min={-1048576}
          max={1048576}
          onChange={(_ev: any, data: any) => {
            const newValue = data?.value ?? _ev.target.value;
            if (newValue !== undefined && newValue !== "") {
              const parsedValue = parseInt(String(newValue), 10);
              if (!isNaN(parsedValue)) {
                setOffset(parsedValue);
              }
            } else {
              setOffset(0);
            }
          }}
        />
      </Field>

      {/* Sélection manuelle ou automatique */}
      <Field className={styles.fieldAlignLeft}>
        <Switch
          checked={checked}
          onChange={onChange}
          label={checked ? "Sélection sur Excel" : "Sélection manuelle"}
        />
        {!checked && (
          <Field label={interfaceShownTextStrings[calledFunction]["manualSelectionTitle"]}>
            <Field className={styles.fieldFlexColumn}>
              <Input
                placeholder="A1"
                size="medium"
                className={styles.inputWidth}
                value={fromCell}
                onChange={handleFromCellChange}
              />
              <Label>À</Label>
              <Input
                placeholder="A6"
                size="medium"
                className={styles.inputWidth}
                value={toCell}
                onChange={handleToCellChange}
              />
            </Field>
          </Field>
        )}
        <Button
          appearance="primary"
          disabled={isDisabled}
          className={`${styles.greenButton}`}
          onClick={StandardiseSelection}
          size="medium"
          icon={buttonIcon}
        >
          {" "}
          {buttonContent}
        </Button>
      </Field>

      {/* Affichage des erreurs */}
      {errors && (
        <div className={styles.errorText}>
          <ErrorCircle16Filled style={{ marginRight: "8px" }} />
          <Text>{errors}</Text>
        </div>
      )}
    </div>
  );
};

export default StandardisationPage;

const useStyles = makeStyles({
  container: {
    padding: "20px",
    textAlign: "center",
    flexDirection: "column",
  },
  text: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  fieldPadding: {
    paddingBottom: "12px",
  },
  fieldAlignLeft: {
    paddingBottom: "12px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    justifyContent: "flex-start",
    gap: "8px",
  },
  fieldFlexColumn: {
    display: "flex",
    flexDirection: "row",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    paddingBottom: "12px",
  },
  buttonAutoWidth: {
    width: "auto",
    height: "auto",
    padding: "8px 16px",
  },
  buttonNonInteractive: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground1,
    cursor: "default",
    pointerEvents: "none",
    [`& .${buttonClassNames.icon}`]: {
      color: tokens.colorStatusSuccessForeground1,
    },
  },
  errorText: {
    color: "red",
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  successText: {
    color: "green",
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  greenButton: {
    backgroundColor: "#217346",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e6d43",
    },
  },
  inputWidth: {
    width: "110px",
  },
  switchLabel: {
    marginTop: "12px",
  },
});
